import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Stack } from '@mui/material';
import { useState, useEffect, useMemo, Dispatch, SetStateAction } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  deleteOurEntitySignatory,
  saveOurEntitySignatory,
  ourEntitySignatoryList,
  saveActivityLog,
  LogEntityType,
  LogContextType,
  LogAction,
} from 'wisestep-api';
import { ControlledTextInput, WsSelect, WsButton } from 'wisestep-base';
import { object, string, array } from 'yup';

import {
  getAvEditAddDes,
  activityLogDTOHelper,
} from '../../../features/activityLog/ActivityHelper';
import { success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import {
  EditIconButton,
  DeleteIconButton,
  SaveIconButton,
} from '../../crmSettings/components/StyledIconButtons';
import IOurEntitySignatory from '../types/IOurEntitySignatory';

interface OurEntityLegalIdentifiersProps {
  setFormWizardCurrentChildStep?: (step: number) => void;
  ourEntityId?: string;
  listPage?: boolean;
  showModal?: Dispatch<SetStateAction<boolean>>;
  loadTabData?: (pageNo: number) => void;
  editObj?: IOurEntitySignatory;
  editFlag?: boolean;
  selectedSignatoryListRecord?: any;
}

const validationSchema = object().shape({
  signatoryData: array()
    .of(
      object().shape({
        firstname: string()
          .required('Please provide First Name')
          .min(3, 'First Name should be atleast 3 characters'),
        lastname: string()
          .required('Please provide Last Name')
          .min(3, 'Last Name should be atleast 3 characters'),
        designation: string().required('Please provide Designation'),
        emailAddress: string().required('Please provide Email Id'),
        type: string().required('Please select Type'),
      })
    )
    .notRequired(),
});

const SignatoryCreate = ({
  ourEntityId,
  listPage = false,
  showModal,
  loadTabData,
  editObj,
  editFlag,
  selectedSignatoryListRecord,
}: OurEntityLegalIdentifiersProps) => {
  const methods = useForm<any>({
    resolver: yupResolver(validationSchema),
  });
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [signatoryData, setSignatoryData] = useState<IOurEntitySignatory[]>([]);

  const onCancel = () => showModal && showModal(false);

  const initialData: IOurEntitySignatory = useMemo(() => {
    return {
      data: {
        id: '',
        firstname: '',
        lastname: '',
        designation: '',
        emailAddress: '',
        ourEntityId: ourEntityId ?? '',
        signatoryImage: '',
        type: '',
      },
      isEditable: true,
    };
  }, [ourEntityId]);

  const {
    handleSubmit,
    formState: { dirtyFields },
  } = methods;

  useEffect(() => {
    const signatoryIdentifierList: IOurEntitySignatory[] = [];
    const getOurSignatoryDataList = async () => {
      if (ourEntityId) {
        const response = await ourEntitySignatoryList(ourEntityId, 0, 100);
        response.data.data?.map((signatoryListData, i) => {
          const signatoryDataObj: IOurEntitySignatory = {
            data: { ...signatoryListData },
            isEditable: false,
          };
          signatoryIdentifierList.push(signatoryDataObj);
          methods.setValue(`signatoryData[${i}].firstname`, signatoryListData.firstname);
          methods.setValue(`signatoryData[${i}].lastname`, signatoryListData.lastname);
          methods.setValue(`signatoryData[${i}].designation`, signatoryListData.designation);
          methods.setValue(`signatoryData[${i}].emailAddress`, signatoryListData.emailAddress);
          methods.setValue(`signatoryData[${i}].signatoryImage`, signatoryListData.signatoryImage);
          methods.setValue(`signatoryData[${0}].type`, signatoryListData.type);
        });
        signatoryIdentifierList.push(initialData);
        setSignatoryData(signatoryIdentifierList);
      }
    };
    if (ourEntityId && !listPage) getOurSignatoryDataList();
    else {
      if (editFlag) {
        signatoryIdentifierList.push(editObj ?? initialData);
        methods.setValue(`signatoryData[${0}].firstname`, editObj?.data.firstname);
        methods.setValue(`signatoryData[${0}].lastname`, editObj?.data.lastname);
        methods.setValue(`signatoryData[${0}].designation`, editObj?.data.designation);
        methods.setValue(`signatoryData[${0}].emailAddress`, editObj?.data.emailAddress);
        methods.setValue(`signatoryData[${0}].signatoryImage`, editObj?.data.signatoryImage);
        methods.setValue(`signatoryData[${0}].type`, editObj?.data.type);

        setSignatoryData(signatoryIdentifierList);
      } else {
        signatoryIdentifierList.push(initialData);
        setSignatoryData(signatoryIdentifierList);
      }
    }
  }, [ourEntityId, initialData, methods, listPage, editFlag, editObj]);

  const onUpdate = async (existingData: IOurEntitySignatory, index: number) => {
    existingData.data.firstname = methods.getValues(`signatoryData[${index}].firstname`);
    existingData.data.lastname = methods.getValues(`signatoryData[${index}].lastname`);
    existingData.data.designation = methods.getValues(`signatoryData[${index}].designation`);
    existingData.data.emailAddress = methods.getValues(`signatoryData[${index}].emailAddress`);
    existingData.data.type = methods.getValues(`signatoryData[${index}].type`);

    existingData.isEditable = false;
    await saveOurEntitySignatory(existingData.data)
      .then((response) => {
        return response.data;
      })
      .then((response) => {
        existingData.data = response.data;
      });
    replaceIndexValue(existingData, index);
  };

  const onSubmit = async (data, index) => {
    const existingData = signatoryData[index];
    existingData.data.firstname = data.signatoryData[index].firstname;
    existingData.data.lastname = data.signatoryData[index].lastname;
    existingData.data.designation = data.signatoryData[index].designation;
    existingData.data.emailAddress = data.signatoryData[index].emailAddress;
    existingData.data.type = data.signatoryData[index].type;
    existingData.isEditable = false;
    const avPayload = { ...activityLogDTOHelper };
    const avChangedValueViaDirtyFileds = Object.keys(dirtyFields).toString().split(',');
    if (existingData.data.id) {
      avPayload.action = LogAction.Edit;

      const avEmptyEntityfields =
        selectedSignatoryListRecord &&
        Object.keys(
          Object.entries(selectedSignatoryListRecord).reduce((acc, [k, v]) => {
            if (v === '' || v === null) acc[k] = v;
            return acc;
          }, {})
        );
      if (
        avPayload &&
        avEmptyEntityfields &&
        avChangedValueViaDirtyFileds &&
        existingData &&
        existingData.data &&
        existingData.data.firstname
      ) {
        avPayload.description = `${getAvEditAddDes(
          avEmptyEntityfields,
          avChangedValueViaDirtyFileds
        )} in ${existingData.data.firstname} Signatory information under `;
      }
    } else {
      avPayload.action = LogAction.Add;
      avPayload.description = `ADDED ${existingData.data.firstname} in Signatory information under`;
    }

    const response = await saveOurEntitySignatory(existingData.data);
    existingData.data = response.data;
    avPayload.parentEntitiyType = LogContextType.OurEntity;
    avPayload.entityType = LogEntityType.Signatory;
    avPayload.entityId = response.data.data.id;
    avPayload.parentEntitiyId = response.data.data.ourEntityId;
    avPayload.field = `Signatory Information`;
    avPayload.entityName = response.data.data.firstname;
    avPayload.contextData = { ourEntityId: response.data.data.ourEntityId };
    avChangedValueViaDirtyFileds[0].length > 0 ? await saveActivityLog(avPayload) : '';
    loadTabData && loadTabData(0);
    if (editFlag) {
      setNotification(success('Our Entity Signatory saved successfully.'));
      showModal && showModal(false);
    } else {
      setNotification(success('Our Entity Signatory saved successfully.'));
      showModal && showModal(false);
    }
  };

  const handleDelete = async (id: string | null, index: number) => {
    if (id) {
      await deleteOurEntitySignatory(id);
    }

    const formElements = methods.getValues('signatoryData');
    formElements.splice(index, 1);
    methods.setValue('signatoryData', formElements);

    const signatoryDataList = [...signatoryData];
    signatoryDataList.splice(index, 1);
    signatoryDataList.map((obj, i) => {
      methods.setValue(`signatoryData[${i}].firstname`, obj.data.firstname);
      methods.setValue(`signatoryData[${i}].lastname`, obj.data.lastname);
      methods.setValue(`signatoryData[${i}].designation`, obj.data.designation);
      methods.setValue(`signatoryData[${i}].emailAddress`, obj.data.emailAddress);
      methods.setValue(`signatoryData[${i}].signatoryImage`, obj.data.signatoryImage);
      methods.setValue(`signatoryData[${i}].type`, obj.data.type);
    });
    setSignatoryData(signatoryDataList);
  };

  const handleEdit = (val: IOurEntitySignatory, index: number) => {
    val.isEditable = true;
    replaceIndexValue(val, index);
  };

  const replaceIndexValue = (val: IOurEntitySignatory, index: number) => {
    const filteredSignatoryIdentifiers = [...signatoryData];
    filteredSignatoryIdentifiers.splice(index, 1, val);
    setSignatoryData([...filteredSignatoryIdentifiers]);
  };

  return (
    <>
      <Box borderRadius={1.5}>
        {signatoryData?.map(function (val, index) {
          return (
            <>
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit((val) => onSubmit(val, index))}>
                  <Grid container py={1}>
                    <Grid item p={1} sm={0.5} sx={{ display: 'flex', justifyContent: 'center' }}>
                      {`${index + 1}.`}
                    </Grid>
                    <Grid container py={1} sm={11}>
                      <Grid item p={1} sm={5.5}>
                        <ControlledTextInput
                          name={`signatoryData[${index}].firstname`}
                          type="text"
                          fullWidth
                          variant="outlined"
                          label="Signatory First Name"
                          showMandatorySymbol={true}
                          disabled={!val.isEditable}
                        />
                      </Grid>
                      <Grid item p={1} sm={5.5}>
                        <ControlledTextInput
                          name={`signatoryData[${index}].lastname`}
                          type="text"
                          fullWidth
                          label="Signatory Last Name"
                          variant="outlined"
                          showMandatorySymbol={true}
                          disabled={!val.isEditable}
                        />
                      </Grid>
                      <Grid item p={1} sm={5.5}>
                        <ControlledTextInput
                          name={`signatoryData[${index}].designation`}
                          type="text"
                          fullWidth
                          label="Signatory Designation"
                          variant="outlined"
                          showMandatorySymbol={true}
                          disabled={!val.isEditable}
                        />
                      </Grid>
                      <Grid item p={1} sm={5.5}>
                        <ControlledTextInput
                          name={`signatoryData[${index}].emailAddress`}
                          type="text"
                          fullWidth
                          label="Signatory Email Address"
                          variant="outlined"
                          showMandatorySymbol={true}
                          disabled={!val.isEditable}
                        />
                      </Grid>
                      <Grid item p={1} sm={5.5}>
                        <WsSelect
                          placeholder="Type"
                          name={`signatoryData[${index}].type`}
                          disabled={!val.isEditable}
                          defaultValue={val.data?.type ?? ''}
                          selectArray={[
                            {
                              label: 'Invoice',
                              value: 'Invoice',
                            },
                            {
                              label: 'Cheque',
                              value: 'Cheque',
                            },
                          ]}
                          size="small"
                        />
                      </Grid>

                      {signatoryData.length != index + 1 ? (
                        <Grid item p={1} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                          {val.isEditable ? (
                            <SaveIconButton onClick={() => onUpdate(val, index)} />
                          ) : (
                            <>
                              <EditIconButton onClick={() => handleEdit(val, index)} />
                              <DeleteIconButton
                                onClick={() => val.data?.id && handleDelete(val.data?.id, index)}
                              />
                            </>
                          )}
                        </Grid>
                      ) : (
                        ''
                      )}
                    </Grid>
                  </Grid>
                  <Stack p={1} columnGap={2} direction="row" justifyContent="flex-end">
                    <WsButton variant="outlined" size="medium" onClick={onCancel}>
                      Cancel
                    </WsButton>
                    {signatoryData.length == index + 1 ? (
                      <WsButton type="submit" variant="contained" size="medium">
                        {editFlag ? 'Update' : `Save`}
                      </WsButton>
                    ) : (
                      ''
                    )}
                  </Stack>
                </form>
              </FormProvider>
            </>
          );
        })}
      </Box>
    </>
  );
};

export default SignatoryCreate;
